/**
 * F60 — the log search filter bar. Edits a local draft and applies it only on
 * Apply / Enter (never per keystroke) — every applied search is a Logs
 * Insights scan. The draft is checked against the shared Zod schema first.
 */
import { useState, type FormEvent } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  OPS_HTTP_METHODS,
  OPS_LOG_LEVELS,
  OpsLogsEventsQuerySchema,
  type OpsLogsFilters,
} from '@bravemove/types';
import {
  Banner,
  Button,
  Chip,
  FormField,
  SearchableSelect,
  Select,
  TextInput,
} from '@bravemove/ui-web';
import { LOGS_PRESETS, rangeForSearch, type LogsPreset, type LogsSearch } from '../opsHelpers';

export interface LogFilterBarProps {
  readonly value: LogsSearch;
  readonly routes: readonly string[];
  readonly disabled?: boolean;
  readonly onApply: (search: LogsSearch) => void;
}

interface Draft {
  preset: LogsPreset;
  /** `datetime-local` values (local time). */
  customFrom: string;
  customTo: string;
  minLevel: string;
  method: string;
  route: string;
  status: string;
  requestId: string;
  userId: string;
  text: string;
}

function toLocalInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const offset = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
}

function draftFrom(search: LogsSearch): Draft {
  const range = rangeForSearch(search);
  return {
    preset: search.preset,
    customFrom: toLocalInput(search.from ?? range.from),
    customTo: toLocalInput(search.to ?? range.to),
    minLevel: search.filters.minLevel ?? '',
    method: search.filters.method ?? '',
    route: search.filters.route ?? '',
    status: search.filters.status ?? '',
    requestId: search.filters.requestId ?? '',
    userId: search.filters.userId ?? '',
    text: search.filters.text ?? '',
  };
}

export function LogFilterBar({ value, routes, disabled = false, onApply }: LogFilterBarProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(value));
  const [invalid, setInvalid] = useState<string | null>(null);
  // Re-seed the draft when the applied search changes from outside (drill-downs).
  const [seededFrom, setSeededFrom] = useState(value);
  if (seededFrom !== value) {
    setSeededFrom(value);
    setDraft(draftFrom(value));
  }

  const set = (key: keyof Draft) => (next: string) => setDraft((d) => ({ ...d, [key]: next }));

  const routeOptions = [
    { value: '', label: t('admin:opsLogs.filters.anyRoute') },
    ...[...new Set(routes.map((r) => r.slice(r.indexOf(' ') + 1)))]
      .sort()
      .map((r) => ({ value: r, label: r })),
  ];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const custom = draft.preset === 'custom';
    const from = custom && draft.customFrom ? new Date(draft.customFrom).toISOString() : undefined;
    const to = custom && draft.customTo ? new Date(draft.customTo).toISOString() : undefined;
    const filters: Record<string, string> = {};
    for (const key of [
      'minLevel',
      'method',
      'route',
      'status',
      'requestId',
      'userId',
      'text',
    ] as const) {
      const v = draft[key].trim();
      if (v) filters[key] = v;
    }
    const search: LogsSearch = {
      preset: custom && from && to ? 'custom' : custom ? '1h' : draft.preset,
      ...(custom && from && to ? { from, to } : {}),
      filters: filters as OpsLogsFilters,
    };
    const check = OpsLogsEventsQuerySchema.safeParse({
      ...rangeForSearch(search),
      ...search.filters,
    });
    if (!check.success) {
      const field = String(check.error.issues[0]?.path[0] ?? '');
      setInvalid(field);
      return;
    }
    setInvalid(null);
    onApply(search);
  };

  const clear = () => {
    const search: LogsSearch = { preset: '1h', filters: {} };
    setDraft(draftFrom(search));
    setInvalid(null);
    onApply(search);
  };

  return (
    <form className="space-y-3" onSubmit={submit} data-testid="ops-log-filters" noValidate>
      <div className="flex flex-wrap items-center gap-2">
        {LOGS_PRESETS.map((p) => (
          <Chip
            key={p}
            selected={draft.preset === p}
            onClick={() => setDraft((d) => ({ ...d, preset: p }))}
            data-testid={`ops-preset-${p}`}
          >
            {t(`admin:opsLogs.presets.${p}`)}
          </Chip>
        ))}
        <Chip
          selected={draft.preset === 'custom'}
          onClick={() => setDraft((d) => ({ ...d, preset: 'custom' }))}
          data-testid="ops-preset-custom"
        >
          {t('admin:opsLogs.presets.custom')}
        </Chip>
      </div>

      {draft.preset === 'custom' ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label={t('admin:opsLogs.filters.from')} htmlFor="ops-from">
            <TextInput
              id="ops-from"
              type="datetime-local"
              size="sm"
              value={draft.customFrom}
              onChange={(e) => set('customFrom')(e.target.value)}
            />
          </FormField>
          <FormField label={t('admin:opsLogs.filters.to')} htmlFor="ops-to">
            <TextInput
              id="ops-to"
              type="datetime-local"
              size="sm"
              value={draft.customTo}
              onChange={(e) => set('customTo')(e.target.value)}
            />
          </FormField>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label={t('admin:opsLogs.filters.minLevel')} htmlFor="ops-level">
          <Select
            id="ops-level"
            size="sm"
            value={draft.minLevel}
            onChange={(e) => set('minLevel')(e.target.value)}
            options={[
              { value: '', label: t('admin:opsLogs.filters.anyLevel') },
              ...OPS_LOG_LEVELS.map((l) => ({ value: l, label: t(`admin:opsLogs.level.${l}`) })),
            ]}
            data-testid="ops-filter-level"
          />
        </FormField>
        <FormField label={t('admin:opsLogs.filters.method')} htmlFor="ops-method">
          <Select
            id="ops-method"
            size="sm"
            value={draft.method}
            onChange={(e) => set('method')(e.target.value)}
            options={[
              { value: '', label: t('admin:opsLogs.filters.anyMethod') },
              ...OPS_HTTP_METHODS.map((m) => ({ value: m, label: m })),
            ]}
          />
        </FormField>
        <FormField label={t('admin:opsLogs.filters.route')}>
          <SearchableSelect
            size="sm"
            value={draft.route}
            onChange={set('route')}
            options={routeOptions}
            placeholder={t('admin:opsLogs.filters.anyRoute')}
            searchPlaceholder={t('admin:opsLogs.filters.routeSearch')}
            emptyText={t('admin:opsLogs.filters.routeEmpty')}
            aria-label={t('admin:opsLogs.filters.route')}
            data-testid="ops-filter-route"
          />
        </FormField>
        <FormField
          label={t('admin:opsLogs.filters.status')}
          htmlFor="ops-status"
          helper={t('admin:opsLogs.filters.statusHelp')}
        >
          <TextInput
            id="ops-status"
            size="sm"
            value={draft.status}
            onChange={(e) => set('status')(e.target.value)}
            invalid={invalid === 'status'}
            data-testid="ops-filter-status"
          />
        </FormField>
        <FormField label={t('admin:opsLogs.filters.requestId')} htmlFor="ops-request">
          <TextInput
            id="ops-request"
            size="sm"
            value={draft.requestId}
            onChange={(e) => set('requestId')(e.target.value)}
            invalid={invalid === 'requestId'}
            data-testid="ops-filter-request"
          />
        </FormField>
        <FormField label={t('admin:opsLogs.filters.userId')} htmlFor="ops-user">
          <TextInput
            id="ops-user"
            size="sm"
            value={draft.userId}
            onChange={(e) => set('userId')(e.target.value)}
            invalid={invalid === 'userId'}
          />
        </FormField>
        <FormField
          label={t('admin:opsLogs.filters.text')}
          htmlFor="ops-text"
          className="sm:col-span-2"
        >
          <TextInput
            id="ops-text"
            size="sm"
            icon="search"
            value={draft.text}
            onChange={(e) => set('text')(e.target.value)}
            invalid={invalid === 'text'}
            data-testid="ops-filter-text"
          />
        </FormField>
      </div>

      {invalid ? (
        <Banner tone="warning">
          {t('admin:opsLogs.filters.invalid', {
            field: t(`admin:opsLogs.filters.fieldNames.${invalid}`, { defaultValue: invalid }),
          })}
        </Banner>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={disabled}
          data-testid="ops-filter-apply"
        >
          {t('admin:opsLogs.filters.apply')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={clear} disabled={disabled}>
          {t('admin:opsLogs.filters.clear')}
        </Button>
      </div>
    </form>
  );
}
